import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import queryString from 'query-string';
import { IPage } from 'interfaces/page.interface';
import ProductList from 'components/products/ProductList';
import { Center, Container, SimpleGrid, useMediaQuery } from '@chakra-ui/react';
import { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { IProductCategory } from 'interfaces/product-category';
import RootLayout from 'layouts/RootLayout';
import { NextPageWithLayout } from 'pages/_app';
import PageHeader from 'components/page-header/PageHeader';
import PageTitleMobile from 'components/page-header/PageTitleMobile';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import useProducts from 'hooks/useProducts';
import { Pagination } from 'antd';

const ProductsPage: NextPageWithLayout = () => {
  const { t } = useTranslation(['product', 'common']);

  // use router
  const router = useRouter();

  // use media query
  const [isTablet] = useMediaQuery('(min-width: 768px)');

  const [isDesktop] = useMediaQuery('(min-width: 992px)');

  // get query from url
  const { query } = router;

  // get paage
  const { data: pageHomeData } = useSWR<IPage>(
    `${process.env.NEXT_PUBLIC_API_URL}/v1/pages/permalink/home`
  );

  // init product new arrival query string
  const [productQueryString, setProductQueryString] = useState<string>('');

  const { productData, isProductsLoading } = useProducts(productQueryString);

  // page title
  const pageTitle = query.type
    ? query.type === 'new-arrival'
      ? t('สินค้าใหม่')
      : query.type === 'best-seller'
      ? t('สินค้าขายดี')
      : query.type === 'spacial-price'
      ? t('สินค้าราคาพิเศษ')
      : ''
    : '';

  useEffect(() => {
    if (pageHomeData) {
      if (pageHomeData.customFields) {
        if (query.type) {
          if (query.type === 'new-arrival') {
            // ********** Product new arrival **********
            const newArrivalProducts = pageHomeData.customFields.find(
              (field) => field.key === 'newArrivalProducts'
            );

            if (newArrivalProducts && !isEmpty(newArrivalProducts)) {
              const newArrivalProductsValue = JSON.parse(
                newArrivalProducts.value
              );

              // log product new arrival value
              console.log('newArrivalProductsValue', newArrivalProductsValue);

              setProductQueryString(
                queryString.stringify({
                  ids: newArrivalProductsValue.join(','),
                  page: 1,
                  limit: isDesktop ? 25 : isTablet ? 24 : 24,
                })
              );
            }
          } else if (query.type === 'best-seller') {
            // ********** Product best seller **********
            const bestSellerProducts = pageHomeData.customFields.find(
              (field) => field.key === 'bestSellerProducts'
            );

            if (bestSellerProducts && !isEmpty(bestSellerProducts)) {
              const bestSellerProductsValue = JSON.parse(
                bestSellerProducts.value
              );

              // log product best seller value
              console.log('bestSellerProductsValue', bestSellerProductsValue);

              setProductQueryString(
                queryString.stringify({
                  ids: bestSellerProductsValue.join(','),
                  page: 1,
                  limit: isDesktop ? 25 : isTablet ? 24 : 24,
                })
              );
            }
          } else if (query.type === 'spacial-price') {
            // ********** Product spacial price **********
            const spacialPriceProducts = pageHomeData.customFields.find(
              (field) => field.key === 'spacialProducts'
            );

            if (spacialPriceProducts && !isEmpty(spacialPriceProducts)) {
              const spacialPriceProductsValue = JSON.parse(
                spacialPriceProducts.value
              );

              // log product spacial price value
              console.log(
                'spacialPriceProductsValue',
                spacialPriceProductsValue
              );

              setProductQueryString(
                queryString.stringify({
                  ids: spacialPriceProductsValue.join(','),
                  page: 1,
                  limit: isDesktop ? 25 : isTablet ? 24 : 24,
                })
              );
            }
          }
        }
      }
    }
  }, [pageHomeData, query, isDesktop, isTablet]);

  console.log('productQueryString', productQueryString);

  // handle pagination change
  const handlePaginationChange = (page: number) => {
    const queryParsedUrl = queryString.parseUrl(window.location.href);

    const queryParsed = queryString.parse(productQueryString);

    // log queryParsed
    console.log('queryParsed', queryParsed);

    const { query } = queryParsed;

    // log query
    console.log('query', query);

    const queryFormatted = {
      ...queryParsed,
      page: page,
    };

    // log queryFormatted
    console.log('queryFormatted', queryFormatted);

    // format query string
    const queryStringFormatted = queryString.stringify(queryFormatted);

    setProductQueryString(queryStringFormatted);
  };

  return (
    <>
      <PageHeader title={pageTitle}></PageHeader>
      <PageTitleMobile>{pageTitle}</PageTitleMobile>
      <Container py={10}>
        <SimpleGrid
          columns={{
            base: 2,
            md: 3,
            lg: 4,
            xl: 5,
          }}
          gap={6}
        >
          <ProductList query={productQueryString} />
        </SimpleGrid>
        {productData && productData?.totalPages > 1 && !isProductsLoading && (
          <Center mt={10}>
            <Pagination
              defaultCurrent={1}
              current={productData.page}
              pageSize={isDesktop ? 25 : isTablet ? 24 : 14}
              total={productData.totalDocs}
              defaultPageSize={isDesktop ? 25 : isTablet ? 24 : 14}
              showLessItems={true}
              showSizeChanger={false}
              onChange={handlePaginationChange}
              className="custom-product-archive-pagination"
            />
          </Center>
        )}
      </Container>
    </>
  );
};

ProductsPage.getLayout = (page) => {
  return <RootLayout>{page}</RootLayout>;
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const _locale = locale || 'th';

  try {
    return {
      props: {
        ...(await serverSideTranslations(_locale, ['product', 'common'])),
      },
      revalidate: 60 * 60 * 24 * 3,
    };
  } catch (error: any) {
    throw new Error(`Failed to fetch posts, received status ${error.message}`);
  }
};

export default ProductsPage;
